// @ts-nocheck
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, Shield, TreeDeciduous, Home, Info, LogIn, Menu, Image, Newspaper, Building2, User, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";

export function Navbar() {
  const { user, isAdmin, isMember, claimStatus, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const NavButton = ({ to, icon: Icon, label, active }: { to: string; icon: any; label: string; active: boolean }) => (
    <Link to={to} onClick={() => setOpen(false)}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          variant="ghost"
          size="sm"
          data-testid={`nav-${to.replace('/', '') || 'home'}`}
          className={`relative rounded-xl px-4 py-2 transition-all duration-300 ${
            active 
              ? 'bg-gradient-to-r from-primary/20 to-primary/10 text-primary border border-primary/30' 
              : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
          }`}
        >
          <Icon className="h-4 w-4 ml-2" />
          <span className="font-tajawal font-medium">{label}</span>
          {active && (
            <motion.div
              layoutId="activeTab"
              className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent rounded-xl -z-10"
              transition={{ type: "spring", duration: 0.5 }}
            />
          )}
        </Button>
      </motion.div>
    </Link>
  );

  const navLinks = (
    <>
      <NavButton to="/" icon={Home} label="الرئيسية" active={isActive("/")} />
      <NavButton to="/about" icon={Info} label="عن العائلة" active={isActive("/about")} />
      <NavButton to="/businesses" icon={Building2} label="شركات العائلة" active={isActive("/businesses")} />
      {user && <NavButton to="/tree" icon={TreeDeciduous} label="شجرة العائلة" active={isActive("/tree")} />}
      <NavButton to="/gallery" icon={Image} label="المعرض" active={isActive("/gallery")} />
      <NavButton to="/news" icon={Newspaper} label="الأخبار" active={isActive("/news")} />

      {!user ? (
        <Link to="/login" onClick={() => setOpen(false)}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              data-testid="nav-login"
              className="relative overflow-hidden rounded-xl px-5 py-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-tajawal font-semibold border-0 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700" />
              <LogIn className="h-4 w-4 ml-2" />
              دخول العائلة
            </Button>
          </motion.div>
        </Link>
      ) : (
        <>
          {isAdmin && <NavButton to="/admin" icon={Shield} label="لوحة التحكم" active={isActive("/admin")} />}
          {isMember && claimStatus === "approved" && !isAdmin && <NavButton to="/my-account" icon={User} label="حسابي" active={isActive("/my-account")} />}

          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              variant="ghost" 
              size="sm" 
              data-testid="nav-logout"
              className="rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-300" 
              onClick={() => { signOut(); setOpen(false); }}
            >
              <LogOut className="h-4 w-4 ml-2" />
              خروج
            </Button>
          </motion.div>
        </>
      )}
    </>
  );

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'glass-nav shadow-lg shadow-black/20' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-18 py-3 px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <motion.div 
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.8 }}
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30"
          >
            <Sparkles className="w-5 h-5 text-primary-foreground" />
          </motion.div>
          <div className="flex flex-col">
            <span className="font-amiri text-2xl gold-text font-bold leading-tight group-hover:opacity-80 transition-opacity">
              عائلة أبوليلة
            </span>
            <span className="text-[10px] text-muted-foreground/60 font-tajawal tracking-wider">
              حفظ التراث والأنساب
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        {!isMobile ? (
          <div className="flex items-center gap-1">
            {navLinks}
          </div>
        ) : (
          /* Mobile Navigation */
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                data-testid="mobile-menu-toggle"
                className="rounded-xl hover:bg-primary/10"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-background/95 backdrop-blur-xl border-primary/10">
              <SheetTitle className="sr-only">القائمة</SheetTitle>
              
              {/* Mobile Menu Header */}
              <div className="flex flex-col items-center py-6 border-b border-primary/10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30 mb-4">
                  <Sparkles className="w-8 h-8 text-primary-foreground" />
                </div>
                <span className="font-amiri text-2xl gold-text font-bold">عائلة أبوليلة</span>
                <span className="text-xs text-muted-foreground mt-1">حفظ التراث والأنساب</span>
              </div>
              
              {/* Mobile Menu Links */}
              <div className="flex flex-col gap-2 mt-6 px-2">
                {navLinks}
              </div>

              {/* Mobile Menu Footer */}
              <div className="absolute bottom-6 left-4 right-4">
                <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent mb-4" />
                <p className="text-center text-xs text-muted-foreground/50">
                  جميع الحقوق محفوظة © 2024
                </p>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </motion.nav>
  );
}
